import { defineStore } from "pinia"
import * as Sentry from "@sentry/vue"
import type { BaseUser, CompanyAdmin } from "@vitable/asclepius-api-client-ts"
import { UserTypeEnum } from "@vitable/asclepius-api-client-ts"

import router from "@/router"
import eventBus from "@common/events/common-event-bus"
import events from "@/modules/login/events/events"
import { useCompanyStore } from "@/store/company-store"

// @ts-ignore
import { useGetMe } from "@/composables/auth"
import { useCompanyContext } from "@/composables/company-context"
import asclepiusConfig from "@common/config/asclepius-config"
import { LOCAL_STORAGE_KEYS } from "@/store/local-storage-keys"

async function _validateAndHydrateSession(store: ReturnType<typeof useUserStore>) {
  await store.reloadMe()

  try {
    const user = store.user
    if (!user) throw new Error("The authenticated user could not be loaded.")
    await useCompanyContext().initializeCompanyContext(user)
    eventBus.$emit(events.LOG_IN)
  } catch (error) {
    Sentry.captureException(error, { tags: { context: "session_hydration" } })
  }
}

export interface UserState {
  user: Nullable<BaseUser>
  token: Nullable<string>
  refreshToken: Nullable<string>
}

export const useUserStore = defineStore("userStore", {
  persist: true,
  state: (): UserState => {
    return {
      token: null,
      refreshToken: null,
      user: null
    }
  },
  getters: {
    isLoggedIn(): boolean {
      return !!(this.user && this.token && this.refreshToken)
    },
    companyAdmin(): Optional<CompanyAdmin> {
      return this.user?.company_admin
    },
    companyId(): string | null | undefined {
      return useCompanyStore().companyId
    },
    isVitableAdmin(): boolean {
      return this.user?.user_type === UserTypeEnum.VitableAdmin
    },
    userId(): string | null | undefined {
      return this.user?.id
    }
  },
  actions: {
    async init() {
      const userStoreCache: string | null = window.localStorage.getItem(LOCAL_STORAGE_KEYS.UserStore)
      if (!userStoreCache) return

      const store: UserState = JSON.parse(userStoreCache)
      if (store.user && store.token && store.refreshToken) {
        this.setAuthTokens(store.token, store.refreshToken)
        this.setUser(store.user)
        try {
          await _validateAndHydrateSession(this)
        } catch {
          this.logout()
        }
      }
    },

    setAuthTokens(accessToken: string, refreshToken: string) {
      this.token = accessToken
      this.refreshToken = refreshToken
      asclepiusConfig.setAccessToken(accessToken)
      asclepiusConfig.setRefreshToken(refreshToken)
    },

    setUser(user: BaseUser) {
      this.user = user
    },

    async reloadMe() {
      const { getMe, me } = useGetMe()
      await getMe()
      if (!me.value) throw new Error("The authenticated user could not be loaded.")
      this.setUser(me.value)
    },

    setCompanyAdmin(companyAdmin: CompanyAdmin) {
      if (this.isLoggedIn && this.user) this.user.company_admin = companyAdmin
    },

    async login(user: BaseUser, accessToken: string, refreshToken: string) {
      this.setAuthTokens(accessToken, refreshToken)
      this.setUser(user)
      await useCompanyContext().initializeCompanyContext(user)
      eventBus.$emit(events.LOG_IN)
    },

    logout() {
      useCompanyContext().cancelCompanyContextOperations()
      useCompanyStore().$reset()
      this.$reset()
      asclepiusConfig.clearAuthTokens()
      router.push({ name: "login" }).then()
      eventBus.$emit(events.LOG_OUT)
    }
  }
})
